import React, { ReactNode, useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { ProgressStepsWrapper } from 'registration/containers/Onboarding';
import { OnboardingData } from 'registration/types';

import { updateTalentProfile } from 'shared/actions/talentProfileActions';
import { ProgressSteps } from 'shared/components/ihcl/progress_steps';
import { styled } from 'shared/components/ihcl/styled';
import { Button } from 'shared/components/ihcl/v2/button';
import {
  trackEvent,
  trackNonHiringOnboardPageView,
} from 'shared/helpers/tracking';
import withGoogleOptimize from 'shared/helpers/withGoogleOptimize';

import { TalentProfile } from 'talent/types';

import {
  convertStateToAttributes,
  convertToState,
  talentProfileAttributesWouldUpdateServer,
} from '../../helpers/talentProfileConversions';
import { getTalentProfile, getTechProfile, getUiState } from '../../selectors';
import {
  OnboardingBackArrow,
  OnboardingTopNavSpacer,
} from './pages/styledComponents';
import { OnboardingStepProvider } from './providers/OnboardingStepProvider';
import { useOnboardingVariantContext } from './providers/OnboardingVariantProvider';

const PASSWORD_STEP_NUMBER = 3;

type SignUpWrapperProps = {
  $justifyFlexStart: boolean;
};
const SignUpWrapper = styled<'div', SignUpWrapperProps>(
  'div',
  ({ $justifyFlexStart }) => ({
    position: 'relative',
    minWidth: '320px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: $justifyFlexStart ? 'flex-start' : 'space-between',
    height: 'calc(var(--vh, 1vh) * 100)',
    overflow: 'auto',
    maxWidth: '560px',
    margin: '0 auto',
    paddingTop: '8px',
    paddingBottom: '120px',
    width: '100%',
    paddingLeft: '16px',
    paddingRight: '16px',
    textAlign: 'center',
    '@media screen and (max-width: 340px)': {
      paddingLeft: 0,
      paddingRight: 0,
    },
  })
);

const setVhProperty = () => {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
};

type OnboardingStepProps = {
  bodyClasses?: string;
  children: ReactNode;
  dispatch: ThunkDispatch<any, undefined, any>;
  justifyFlexStart?: boolean;
  serverTalentProfile: TalentProfile;
  stepName: string;
  uiState: Map<string, any>;
};

const OnboardingStep = ({
  children,
  dispatch,
  justifyFlexStart = false,
  bodyClasses = '',
  serverTalentProfile = null,
  stepName,
  uiState,
}: OnboardingStepProps) => {
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(
    uiState.get('profileSaving')
  );
  const {
    currentStep,
    goToPreviousPage,
    intent,
    onboardVariant,
    setMostRecentCompletedStep,
    setTalentProfileUpdates,
    talentProfileUpdates,
    totalSteps,
  } = useOnboardingVariantContext();
  const [localStepTalentProfile, setLocalStepTalentProfile] =
    useState<OnboardingData>(talentProfileUpdates);
  const [talentProfileKeys, setTalentProfileKeys] = useState(
    () => new Set<string>()
  );
  const addTalentProfileKeys = (keys) => {
    const missingKeys = keys.filter((key) => !talentProfileKeys.has(key));
    if (missingKeys) {
      setTalentProfileKeys(
        (prev) => new Set([...Array.from(prev), ...missingKeys])
      );
    }
  };

  const sharedEventProperties = {
    intent,
    onboard_variant: onboardVariant,
  };

  useEffect(() => {
    setVhProperty();
    trackNonHiringOnboardPageView();
    trackEvent(`Onboard Lite Step ${stepName} Started`, sharedEventProperties);
  }, [stepName]);

  useEffect(() => {
    if (isUpdatingProfile !== uiState.get('profileSaving')) {
      setIsUpdatingProfile(uiState.get('profileSaving'));
    }
  }, [isUpdatingProfile, uiState]);

  useEffect(() => {
    setLocalStepTalentProfile((localTp) => ({
      ...convertToState(serverTalentProfile, talentProfileKeys),
      ...talentProfileUpdates,
      ...localTp,
    }));
  }, [serverTalentProfile, talentProfileUpdates, talentProfileKeys]);

  const completeStep = (newTalentProfileUpdates) => {
    setTalentProfileUpdates(newTalentProfileUpdates);
    trackEvent(
      `Onboard Lite Step ${stepName} Completed`,
      sharedEventProperties
    );
    setMostRecentCompletedStep(currentStep);
  };
  const maybeGoToNext = () => {
    const updatedTalentProfile = {
      ...talentProfileUpdates,
      ...localStepTalentProfile,
    };
    const profileAttributes = convertStateToAttributes(
      updatedTalentProfile,
      Object.keys(updatedTalentProfile),
      serverTalentProfile
    );
    const shouldUpdateTalentProfile = talentProfileAttributesWouldUpdateServer(
      profileAttributes,
      serverTalentProfile
    );

    if (serverTalentProfile.id != null && shouldUpdateTalentProfile) {
      // Update the server with the new talent profile attributes.
      let profileAttributesWithStep = profileAttributes;
      if (currentStep > serverTalentProfile.current_onboard_step) {
        profileAttributesWithStep = {
          ...profileAttributes,
          current_onboard_step: currentStep,
        };
      }
      setIsUpdatingProfile(true);
      dispatch(
        updateTalentProfile(
          serverTalentProfile.id,
          profileAttributesWithStep,
          () => {
            setIsUpdatingProfile(false);
            completeStep({});
          }
        )
      );
    } else if (serverTalentProfile.id != null) {
      // The talent profile is already up to date with the server talent profile
      completeStep({});
    } else {
      // Roll over the updated attributes to the next page.
      completeStep(updatedTalentProfile);
    }
  };

  return (
    <OnboardingStepProvider
      config={{
        addTalentProfileKeys,
        completeStep,
        isUpdatingProfile,
        localStepTalentProfile,
        maybeGoToNext,
        maybeGoToPrevious: goToPreviousPage,
        setLocalStepTalentProfile,
      }}
    >
      <div>
        <Helmet>
          <body className={`${bodyClasses} registration-app`} />
        </Helmet>
        <SignUpWrapper $justifyFlexStart={justifyFlexStart}>
          <ProgressStepsWrapper $accommodateBackButton>
            {currentStep > PASSWORD_STEP_NUMBER && (
              <Button
                type="button"
                variant="minimal"
                color="alternate"
                onClick={goToPreviousPage}
                data-testid="onboarding-compact-back-button"
              >
                <OnboardingBackArrow name="ArrowLeft" />
              </Button>
            )}
            {currentStep < 4 && <OnboardingTopNavSpacer />}
            <ProgressSteps currentStep={currentStep} totalSteps={totalSteps} />
            <OnboardingTopNavSpacer />
          </ProgressStepsWrapper>
          {children}
        </SignUpWrapper>
      </div>
    </OnboardingStepProvider>
  );
};

const mapStateToProps = (state) => ({
  serverTalentProfile: getTalentProfile(state) || getTechProfile(state),
  uiState: getUiState(state),
});

export default withGoogleOptimize(connect(mapStateToProps)(OnboardingStep));
